import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Logo } from "components/logo"
import { useAuth } from "hooks/use-auth"
import { PageLoader } from "@/components/page-loader"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { trpc } from "@/lib/trpc"
export const Route = createFileRoute("/_public/sign-up/pro/")({
  component: BetaOnboarding,
})

function BetaOnboarding() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()

  const { data: user, isLoading: meLoading } = trpc.users.me.useQuery(undefined, {
    enabled: isAuthenticated,
  })

  const isProfessional = user?.userType === "professional"

  // Stripe Checkout Session mutation
  const createCheckoutSession = trpc.stripe.createCheckoutSession.useMutation()

  // Handle checkout cancel
  const checkoutParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("checkout")
      : null

  const handleStartAsGuest = () => {
    navigate({
      to: "/sign-up",
      search: { redirect: "/sign-up/pro" },
    })
  }

  const handleStartCheckout = async () => {
    try {
      const origin = window.location.origin
      const successUrl = `${origin}/dashboard?checkout=success`
      const cancelUrl = `${origin}/sign-up/pro?checkout=cancel`

      const { url } = await createCheckoutSession.mutateAsync({
        mode: "subscription",
        successUrl,
        cancelUrl,
      })

      window.location.href = url
    } catch {
      // surfaced via createCheckoutSession.error below
    }
  }

  if (authLoading || meLoading) {
    return <PageLoader />
  }

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-8 p-6 md:py-10 md:px-0 items-center justify-center">
        <div className="w-full max-w-xs space-y-7">
          <div className="justify-center flex">
            <Logo />
          </div>

          <h1 className="text-3xl font-light text-center">
            Welcome to <br />
            <span className="font-bold">Legacy Keeper</span> <br />
            <span className="text-teal-600 italic">Pro</span>
          </h1>

          <div className="space-y-4 text-center">
            <h2 className="text-xl">
              Access the Legacy Keeper professional dashboard to gain estate planning insights for
              individual clients
            </h2>

            <p className="text-sm text-center">
              {!isAuthenticated ? (
                <span>Sign up to start 7 day free trial.</span>
              ) : (
                <>
                  <a
                    href="https://app.apollo.io/#/meet/jon_mason_ca6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 underline hover:text-teal-700"
                  >
                    Book a demo
                  </a>{" "}
                  to access:
                </>
              )}
            </p>

            <ul className="space-y-3 text-sm">
              <li className="bg-teal-50 border-2 border-teal-400 rounded-sm p-3">
                <strong>Extended trial</strong> for additional testing.
              </li>
              <li className="bg-teal-50 border-2 border-teal-400 rounded-sm p-3">
                <strong>Premium Support</strong> set onboarding meetings for additional guidance and
                support.
              </li>
              <li className="bg-teal-50 border-2 border-teal-400 rounded-sm p-3">
                <strong>Up To 50% Discounts</strong> in exchange for feedback.
              </li>
            </ul>
          </div>

          {/* Not logged in → go sign up first */}
          {!isAuthenticated && (
            <Button size="lg" className="w-full" onClick={handleStartAsGuest}>
              Sign up to get started
            </Button>
          )}

          {/* Logged in but not professional */}
          {isAuthenticated && !isProfessional && (
            <>
              <Alert variant="destructive">
                <AlertDescription>
                  This beta offer is only available for professional accounts.
                </AlertDescription>
              </Alert>
              <Button
                size="lg"
                className="w-full"
                onClick={() =>
                  navigate({
                    to: "/dashboard",
                    search: { token: undefined },
                  })
                }
              >
                Go to dashboard
              </Button>
            </>
          )}

          {/* Logged in + professional */}
          {isAuthenticated && isProfessional && (
            <>
              {checkoutParam === "cancel" ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    Checkout was cancelled. You can try again whenever you're ready.
                  </AlertDescription>
                </Alert>
              ) : null}

              {checkoutParam === "success" ? (
                <Alert>
                  <AlertDescription>
                    Thanks! If your subscription doesn't update immediately, refresh in a moment.
                  </AlertDescription>
                </Alert>
              ) : null}

              {/* Backend/Stripe error */}
              {createCheckoutSession.error && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {createCheckoutSession.error.message ||
                      "Unable to start checkout. Please try again later."}
                  </AlertDescription>
                </Alert>
              )}

              <Button
                size="lg"
                className="w-full"
                onClick={handleStartCheckout}
                disabled={createCheckoutSession.isPending}
              >
                {createCheckoutSession.isPending ? "Redirecting to Stripe..." : "Start free trial"}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-sky-100 p-12 flex items-center justify-center">
        <img src="/images/login_illustration.svg" alt="Illustration" className="object-contain" />
      </div>
    </div>
  )
}
